import ButtonT from '@components/tailwind-components/Button/ButtonT';
import InputWithLabelT from '@components/tailwind-components/Input/InputWithLabelT';
import LabelT from '@components/tailwind-components/Label/LabelT';
import FileUploadT from '@components/tailwind-components/Upload/FileUploadT';
import type { CreateCompanyMemberDTO } from '@conversed/rest/src/contracts/company/company.contract';
import type { TFile } from '@conversed/shared/src/central/types/common.type';
import type React from 'react';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface Props {
  onSubmit: (data?: CreateCompanyMemberDTO) => void;
  onBack: () => void;
}
const CompanyMemberForm: React.FC<Props> = ({ onSubmit, onBack }) => {
  const { handleSubmit, control } = useForm<CreateCompanyMemberDTO>({
    shouldUseNativeValidation: true,
    defaultValues: {
      firstName: '',
      email: '',
      lastName: '',
      profilePic: null,
      roles: [],
    },
  });
  const { t } = useTranslation();
  const [memberProfilePic, setMemberProfilePic] = useState<TFile | null>(null);

  const handleFormSubmit = useCallback(
    (data: CreateCompanyMemberDTO) => {
      if (data) {
        return onSubmit({ ...data, profilePic: memberProfilePic });
      }
    },
    [memberProfilePic, onSubmit],
  );

  const handleFileUpload = (file: TFile | string) => {
    if (typeof file !== 'string' && 'base64' in file) {
      return setMemberProfilePic(file);
    }

    return setMemberProfilePic(null);
  };

  const createCompanyWithoutAdminUser = () => {
    return onSubmit();
  };

  return (
    <form className='w-full' onSubmit={handleSubmit(handleFormSubmit)}>
      <div className='grid grid-cols-1 gap-x-6 gap-y-2'>
        <div className='col-span-1'>
          <InputWithLabelT
            type='text'
            field='firstName'
            placeholder={t('new-customer.company-member.first-name')}
            label={t('new-customer.company-member.first-name')}
            control={control}
            options={{
              required: t('general.validation.required'),
            }}
          />
        </div>
        <div className='col-span-1'>
          <InputWithLabelT
            type='text'
            field='lastName'
            placeholder={t('new-customer.company-member.last-name')}
            label={t('new-customer.company-member.last-name')}
            control={control}
            options={{
              required: t('general.validation.required'),
            }}
          />
        </div>
        <div className='col-span-1'>
          <InputWithLabelT
            type='email'
            field='email'
            placeholder={t('new-customer.company-member.email')}
            label={t('new-customer.company-member.email')}
            control={control}
            options={{
              required: t('validation.email'),
            }}
          />
        </div>
        <div className='col-span-1'>
          <LabelT text={t('new-customer.company-member.profile-pic')} />
          <p className='text-xs italic mt-3'>
            {t('component.upload.by.imageUrl.description')}
          </p>

          <FileUploadT
            accept={['image/*']}
            align='row'
            onFileUpload={handleFileUpload}
          />
        </div>
      </div>

      <div className='flex justify-between gap-3'>
        <ButtonT
          type='button'
          styling='secondary'
          size='xs'
          rounding='md'
          className='mt-3'
          onClick={onBack}>
          {t('general.buttons.back')}
        </ButtonT>
        <div className='flex gap-3'>
          <ButtonT
            type='button'
            styling='primary'
            size='xs'
            rounding='md'
            className='mt-3'
            onClick={() => createCompanyWithoutAdminUser()}>
            {t('general.buttons.skip')}
          </ButtonT>
          <ButtonT
            type='submit'
            styling='primary'
            size='xs'
            rounding='md'
            className='mt-3'>
            {t('general.buttons.finish')}
          </ButtonT>
        </div>
      </div>
    </form>
  );
};

export default CompanyMemberForm;
